(() => {
  'use strict';

  const config = window.distopionCartThresholdConfig;
  const thresholds = Array.isArray(config?.thresholds) ? config.thresholds : [];
  const language = String(document.documentElement.lang || '')
    .toLowerCase()
    .split('-')[0];
  const isGerman = language === 'de';
  const isItalian = language === 'it';
  const isLocalized = isGerman || isItalian;

  if (thresholds.length === 0) return;

  const shopCurrency = String(config?.shopCurrency || '').toUpperCase();
  const activeCurrency = String(
    window.Shopify?.currency?.active || shopCurrency || 'EUR'
  ).toUpperCase();
  const configuredRate = Number(window.Shopify?.currency?.rate);
  const currencyRate =
    activeCurrency === shopCurrency
      ? 1
      : Number.isFinite(configuredRate) && configuredRate > 0
        ? configuredRate
        : 1;

  const convertedThresholds = thresholds.map((threshold) => ({
    amount: Math.ceil(Number(threshold?.amount || 0) * currencyRate),
    label: String(threshold?.label || ''),
  }));

  const detectedShippingIndex = thresholds.findIndex((threshold) =>
    /\b(shipping|delivery|versand|spedizione|consegna)\b/i.test(
      String(threshold?.label || '')
    )
  );

  // The verified offer ladder is €100 Distopion pouch, then €150 free
  // shipping. Machine translation can rename tier one incorrectly, so use
  // the actual configured tier order for German and Italian storefronts.
  const shippingIndex =
    isLocalized && thresholds.length > 1
      ? 1
      : detectedShippingIndex >= 0
        ? detectedShippingIndex
        : thresholds.length === 1
          ? 0
          : -1;

  if (shippingIndex < 0) return;

  const copy = isGerman
    ? {
        shipping: 'Versand',
        free: 'Kostenlos',
        total: 'Gesamt',
        shippingUnlocked: 'Kostenloser Versand freigeschaltet',
        allBenefits: 'Alle Vorteile freigeschaltet.',
      }
    : isItalian
      ? {
          shipping: 'Spedizione',
          free: 'Gratuita',
          total: 'Totale',
          shippingUnlocked: 'Spedizione gratuita sbloccata',
          allBenefits: 'Tutti i vantaggi sono sbloccati.',
        }
      : {
          shipping: 'Shipping',
          free: 'Free',
          total: 'Total',
          shippingUnlocked: 'Free shipping unlocked',
          allBenefits: "You've unlocked all rewards!",
        };

  const numberLocale = isGerman ? 'de-DE' : isItalian ? 'it-IT' : 'en-US';

  let frame = 0;
  let settleTimer = 0;
  let finalTimer = 0;
  let translationTimer = 0;

  const totalLabelSelector = [
    'cart-drawer .cart-drawer__footer-inner .h-stack.justify-between.gap-4 > span.medium.uppercase:first-child',
    'cart-drawer .cart-drawer__footer-inner .h-stack.justify-between.gap-4 > span.medium:first-child',
    '.shopify-section--main-cart .cart__right .h-stack.justify-between.gap-4 > span.medium:first-child',
  ].join(',');

  const exactGermanCopy = new Map([
    ['You may also like', 'Das könnte dir auch gefallen'],
    ['Das könnte Ihnen auch gefallen', 'Das könnte dir auch gefallen'],
    ['Complimentary Distopion Pouch', 'Kostenlose Distopion-Pouch'],
    ['Complimentary Pouch', 'Kostenlose Distopion-Pouch'],
    ['Distopion pouch', 'Kostenlose Distopion-Pouch'],
    ['Complimentary', 'Kostenlos'],
    ['Free shipping', 'Kostenloser Versand'],
    ['Free gift', 'Gratisgeschenk'],
    ['10% off', '10 % Rabatt'],
    ['10% Off', '10 % Rabatt'],
    ['Offers', 'Angebote'],
    ['Shipping', 'Versand'],
    ['Free', 'Kostenlos'],
    ['Total', 'Gesamt'],
    ['You are eligible for free shipping.', 'Kostenloser Versand ist freigeschaltet.'],
    ['Deine Bestellung ist für kostenlosen Versand qualizifiert.', 'Kostenloser Versand ist freigeschaltet.'],
    ['Deine Bestellung ist für kostenlosen Versand qualifiziert.', 'Kostenloser Versand ist freigeschaltet.'],
    ["You've unlocked all rewards!", 'Alle Vorteile freigeschaltet.'],
    ['Du hast alle Prämien freigeschaltet!', 'Alle Vorteile freigeschaltet.'],
  ]);

  const exactItalianCopy = new Map([
    ['You may also like', 'Potrebbe piacerti anche'],
    ['Potrebbero piacerti anche', 'Potrebbe piacerti anche'],
    ['Ti potrebbe piacere anche', 'Potrebbe piacerti anche'],
    ['Potrebbe piacerti', 'Potrebbe piacerti anche'],
    ['Complimentary Distopion Pouch', 'Pochette Distopion in omaggio'],
    ['Complimentary Pouch', 'Pochette Distopion in omaggio'],
    ['Distopion pouch', 'Pochette Distopion in omaggio'],
    ['Pochette Distopion gratuita', 'Pochette Distopion in omaggio'],
    ['Pochette Distopion omaggio', 'Pochette Distopion in omaggio'],
    ['Complimentary', 'In omaggio'],
    ['Free shipping', 'Spedizione gratuita'],
    ['Free gift', 'Regalo in omaggio'],
    ['10% off', '10% di sconto'],
    ['10% Off', '10% di sconto'],
    ['Offers', 'Offerte'],
    ['Shipping', 'Spedizione'],
    ['Free', 'Gratuita'],
    ['Total', 'Totale'],
    ['You are eligible for free shipping.', 'Hai sbloccato la spedizione gratuita.'],
    ['Hai diritto alla spedizione gratuita.', 'Hai sbloccato la spedizione gratuita.'],
    ['Il tuo ordine è idoneo per la spedizione gratuita.', 'Hai sbloccato la spedizione gratuita.'],
    ["You've unlocked all rewards!", 'Tutti i vantaggi sono sbloccati.'],
    ['Hai sbloccato tutti i premi!', 'Tutti i vantaggi sono sbloccati.'],
  ]);

  const escapeHtml = (value) =>
    String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const formatMoney = (minorUnits) => {
    const amount = Math.max(0, Number(minorUnits || 0)) / 100;

    try {
      return new Intl.NumberFormat(numberLocale, {
        style: 'currency',
        currency: activeCurrency,
        currencyDisplay: 'symbol',
      }).format(amount);
    } catch (_error) {
      return `${amount.toFixed(2).replace('.', ',')} ${activeCurrency}`;
    }
  };

  const localizedTierLabel = (index) => {
    if (isGerman) {
      if (index === 0) return 'Kostenlose Distopion-Pouch';
      if (index === shippingIndex) return 'Kostenloser Versand';

      const source = String(thresholds[index]?.label || '');
      if (/10\s*%/i.test(source)) return '10 % Rabatt';
      if (/gift|geschenk/i.test(source)) return 'Gratisgeschenk';
      return source;
    }

    if (isItalian) {
      if (index === 0) return 'Pochette Distopion in omaggio';
      if (index === shippingIndex) return 'Spedizione gratuita';

      const source = String(thresholds[index]?.label || '');
      if (/10\s*%/i.test(source)) return '10% di sconto';
      if (/gift|regalo/i.test(source)) return 'Regalo in omaggio';
      return source;
    }

    return String(thresholds[index]?.label || '');
  };

  const translateDrawerText = () => {
    if (!isLocalized) return;

    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    const dictionary = isGerman ? exactGermanCopy : exactItalianCopy;
    const walker = document.createTreeWalker(drawer, NodeFilter.SHOW_TEXT);
    const nodes = [];

    while (walker.nextNode()) nodes.push(walker.currentNode);

    nodes.forEach((node) => {
      const parentTag = node.parentElement?.tagName;
      if (!parentTag || parentTag === 'SCRIPT' || parentTag === 'STYLE') return;

      const raw = node.nodeValue || '';
      const trimmed = raw.trim();
      if (!trimmed) return;

      let translated = dictionary.get(trimmed);

      if (!translated && /^10\s*%\s*off$/i.test(trimmed)) {
        translated = isGerman ? '10 % Rabatt' : '10% di sconto';
      }

      if (!translated && isGerman && /^(gesamtbetrag|gesamt)$/i.test(trimmed)) {
        translated = 'Gesamt';
      }

      if (
        !translated &&
        isItalian &&
        /^(totale complessivo|totale ordine|totale)$/i.test(trimmed)
      ) {
        translated = 'Totale';
      }

      if (!translated) return;

      const leading = raw.match(/^\s*/)?.[0] || '';
      const trailing = raw.match(/\s*$/)?.[0] || '';
      node.nodeValue = `${leading}${translated}${trailing}`;
    });
  };

  const currentTotalForBar = (bar) => {
    const savedTotal = Number(bar.dataset.distopionCurrentTotal);
    if (Number.isFinite(savedTotal)) return savedTotal;

    if (bar.classList.contains('free-shipping-bar--tiers')) {
      return Array.from(
        bar.querySelectorAll('.free-shipping-bar__tier-fill[aria-valuenow]')
      ).reduce(
        (total, progress) =>
          total + Number(progress.getAttribute('aria-valuenow') || 0),
        0
      );
    }

    const progress = bar.querySelector(
      '#progress-bar-free-shipping-threshold, .progress-bar[aria-valuenow]'
    );

    return Number(progress?.getAttribute('aria-valuenow') || 0);
  };

  const localizedProgressMessage = (nextIndex, remaining) => {
    const safeRemaining = escapeHtml(remaining);

    if (isGerman) {
      if (nextIndex === 0) {
        return (
          `Noch <span class="bold">${safeRemaining}</span> bis zur ` +
          '<span class="bold">kostenlosen Distopion-Pouch</span>'
        );
      }

      if (nextIndex === shippingIndex) {
        return (
          `Noch <span class="bold">${safeRemaining}</span> bis zum ` +
          '<span class="bold">kostenlosen Versand</span>'
        );
      }

      return (
        `Noch <span class="bold">${safeRemaining}</span> bis ` +
        `<span class="bold">${escapeHtml(localizedTierLabel(nextIndex))}</span>`
      );
    }

    if (isItalian) {
      if (nextIndex === 0) {
        return (
          `Ancora <span class="bold">${safeRemaining}</span> per la ` +
          '<span class="bold">pochette Distopion in omaggio</span>'
        );
      }

      if (nextIndex === shippingIndex) {
        return (
          `Ancora <span class="bold">${safeRemaining}</span> per la ` +
          '<span class="bold">spedizione gratuita</span>'
        );
      }

      return (
        `Ancora <span class="bold">${safeRemaining}</span> per ` +
        `<span class="bold">${escapeHtml(localizedTierLabel(nextIndex))}</span>`
      );
    }

    return '';
  };

  const updateLocalizedBenefitBar = (bar) => {
    if (!isLocalized) return;

    const currentTotal = currentTotalForBar(bar);
    const nextIndex = convertedThresholds.findIndex(
      (threshold) => currentTotal < threshold.amount
    );
    const message =
      bar.querySelector('.free-shipping-bar__message') ||
      bar.querySelector(':scope > span');

    if (message) {
      if (nextIndex < 0) {
        message.textContent = copy.allBenefits;
      } else {
        const remaining = formatMoney(
          convertedThresholds[nextIndex].amount - currentTotal
        );
        message.innerHTML = localizedProgressMessage(nextIndex, remaining);
      }
    }

    const amountLeft = bar
      .closest('cart-drawer')
      ?.querySelector('[data-distopion-amount-left]');

    if (amountLeft && message) {
      amountLeft.innerHTML = message.innerHTML;
      amountLeft.classList.toggle('is-complete', nextIndex < 0);
    }

    const tierItems = bar.querySelectorAll('.free-shipping-bar__tier');
    tierItems.forEach((tier, index) => {
      const label = localizedTierLabel(index);
      const labelElement = tier.querySelector('.free-shipping-bar__tier-label');
      const progress = tier.querySelector('.free-shipping-bar__tier-fill');

      if (labelElement) labelElement.textContent = label;
      if (label) tier.setAttribute('title', label);
      if (progress && label) progress.setAttribute('aria-label', label);
    });

    const milestoneItems = bar.querySelectorAll('.free-shipping-bar__milestone');
    milestoneItems.forEach((milestone, index) => {
      const label = localizedTierLabel(index);
      const labelElement = milestone.querySelector(
        '.free-shipping-bar__milestone-label'
      );

      if (labelElement) labelElement.textContent = label;
    });
  };

  const ensureSummaryRows = () => {
    const rows = [];

    document.querySelectorAll(totalLabelSelector).forEach((totalLabel) => {
      const totalRow = totalLabel.parentElement;
      const container = totalRow?.parentElement;

      if (!totalRow || !container) return;

      totalLabel.classList.remove('uppercase');

      const currentText = totalLabel.textContent.trim();
      const shouldNormalizeGerman =
        isGerman && /^(total|gesamtbetrag|gesamt)$/i.test(currentText);
      const shouldNormalizeItalian =
        isItalian &&
        /^(total|totale|totale complessivo|totale ordine)$/i.test(currentText);

      if (shouldNormalizeGerman || shouldNormalizeItalian) {
        totalLabel.textContent = copy.total;
      }

      let summary = container.querySelector(
        ':scope > .distopion-shipping-summary'
      );

      if (!summary) {
        summary = document.createElement('div');
        summary.className =
          'distopion-shipping-summary h-stack justify-between gap-4 text-xs';
        summary.setAttribute('aria-live', 'polite');
        summary.style.display = 'none';
        totalRow.before(summary);
      }

      summary.setAttribute('aria-label', copy.shippingUnlocked);
      summary.innerHTML =
        `<span>${copy.shipping}</span><span class="medium">${copy.free}</span>`;

      rows.push(summary);
    });

    return rows;
  };

  const shippingIsUnlocked = () => {
    const bars = document.querySelectorAll('free-shipping-bar');

    for (const bar of bars) {
      if (bar.classList.contains('free-shipping-bar--tiers')) {
        const tier = bar.querySelectorAll('.free-shipping-bar__tier')[shippingIndex];
        if (tier) return tier.classList.contains('is-complete');
      }

      if (bar.classList.contains('free-shipping-bar--milestones')) {
        const milestone = bar.querySelectorAll(
          '.free-shipping-bar__milestone'
        )[shippingIndex];
        if (milestone) return milestone.classList.contains('is-reached');
      }

      if (shippingIndex === 0) {
        const progress = bar.querySelector(
          '#progress-bar-free-shipping-threshold, .progress-bar[aria-valuenow]'
        );
        const now = Number(progress?.getAttribute('aria-valuenow') || 0);
        const max = Number(progress?.getAttribute('aria-valuemax') || 0);

        if (max > 0) return now >= max;
      }
    }

    return false;
  };

  const sync = () => {
    translateDrawerText();

    document
      .querySelectorAll('cart-drawer free-shipping-bar')
      .forEach(updateLocalizedBenefitBar);

    const unlocked = shippingIsUnlocked();

    ensureSummaryRows().forEach((row) => {
      row.style.display = unlocked ? '' : 'none';
      row.setAttribute('aria-hidden', String(!unlocked));
    });

    translateDrawerText();
  };

  const schedule = () => {
    if (!frame) {
      frame = requestAnimationFrame(() => {
        frame = 0;
        sync();
      });
    }

    window.clearTimeout(settleTimer);
    settleTimer = window.setTimeout(sync, 240);

    window.clearTimeout(finalTimer);
    finalTimer = window.setTimeout(sync, 560);

    // Translation apps can finish after the section renders. This final scoped
    // pass prevents stale translated phrases from replacing verified offer copy.
    window.clearTimeout(translationTimer);
    translationTimer = window.setTimeout(sync, 1200);
  };

  const start = () => {
    schedule();

    document.addEventListener('cart:change', schedule, { passive: true });
    document.addEventListener('cart:refresh', schedule, { passive: true });
    document.addEventListener('shopify:section:load', schedule, {
      passive: true,
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.addEventListener('pageshow', schedule, { passive: true });
})();
